import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IActivityLogsData } from "../../api/agentActivityLog/index.types";
import {
  IAccountNote,
  IGetAccountManagementDataResponse,
  IGetCustomerPlansResponse,
  IGetCustomerResponse,
} from "../../api/accounts/index.types";
import { IGetCustomerPaymentCardsResponse, IPaymentCard } from "../../api/paymentCards/index.types";
import { IGetSharedRegIpsResponse, TGetSharedIpsResponse } from "../../api/logins/index.types";
import { IGetCustomerComplianceNotesResponse } from "../../api/compliance/index.types";
import { IFindSimilarFundedAccountsResponse } from "../../api/funds/index.types";

export interface ICustomerState {
  complianceNotes: {
    data: IGetCustomerComplianceNotesResponse["data"];
    loading: boolean;
  };
  suspiciousNotes: {
    data: {
      notes: IAccountNote[];
      count: number;
      countActive: number;
    };
    loading: boolean;
  };
  notes: {
    data: {
      notes: IAccountNote[];
      count: number;
      countActive: number;
    };
    loading: boolean;
  };
  agentLogs: {
    data: {
      logs: IActivityLogsData[];
      count: number;
    };
    loading: boolean;
  };
  plans: {
    data: IGetCustomerPlansResponse["data"];
    loading: boolean;
  };
  managementData: {
    data: IGetAccountManagementDataResponse["data"];
    loading: boolean;
  };
  payment_cards: {
    loading: boolean;
    data: {
      payment_cards: {
        cards: IPaymentCard[];
        count: number;
      };
    };
  };
  mainData: {
    loading: boolean;
    data: {
      banned_account: IGetCustomerResponse["data"]["banned_account"] | null;
      funded_trader: IGetCustomerResponse["data"]["funded_trader"] | null;
    };
  };
  ipsData: {
    sharedIps: {
      loading: boolean;
      data: TGetSharedIpsResponse["data"]["sharedIps"];
    };
    sharedRegIp: {
      loading: boolean;
      data: IGetSharedRegIpsResponse["data"]["sharedRegIp"];
    };
  };
  similarTradedAccounts: {
    data: IFindSimilarFundedAccountsResponse["data"];
    loading: boolean;
  };
}

const initialState: ICustomerState = {
  complianceNotes: {
    data: {
      count: 0,
      rows: [],
    },
    loading: false,
  },
  suspiciousNotes: {
    data: {
      count: 0,
      countActive: 0,
      notes: [],
    },
    loading: false,
  },
  notes: {
    data: {
      count: 0,
      countActive: 0,
      notes: [],
    },
    loading: false,
  },
  agentLogs: {
    data: {
      logs: [],
      count: 0,
    },
    loading: false,
  },
  plans: {
    data: {
      passedPlan: null,
      activePlan: null,
      plans: [],
      billing_history_by_plans: [],
      count: 0,
    },
    loading: false,
  },
  managementData: {
    loading: false,
    data: {
      account_gauntlets: [],
      account_plans: [],
      account_simulators: [],
      status: 0,
      id: -1,
    },
  },
  payment_cards: {
    loading: false,
    data: {
      payment_cards: {
        cards: [],
        count: 0,
      },
    },
  },
  mainData: {
    loading: false,
    data: {
      banned_account: null,
      funded_trader: null,
    },
  },
  ipsData: {
    sharedIps: {
      loading: false,
      data: {
        count: 0,
        rows: [],
      },
    },
    sharedRegIp: {
      loading: false,
      data: {
        count: 0,
        rows: [],
      },
    },
  },
  similarTradedAccounts: {
    loading: false,
    data: {
      evaluation_completed_history: [],
      fundedTrader: null,
      fundedTradersList: [],
      similarAccountsBasedOnNameAddressAndPhone: [],
    },
  },
};

const index = createSlice({
  name: "customer",
  initialState,
  reducers: {
    // Agent Logs
    fetchAgentLogsRequest: (state) => {
      state.agentLogs.loading = true;
    },
    fetchAgentLogsSuccess: (
      state,
      action: PayloadAction<{ logs: IActivityLogsData[]; count: number }>
    ) => {
      state.agentLogs.data.logs = action.payload.logs;
      state.agentLogs.data.count = action.payload.count;
      state.agentLogs.loading = false;
    },
    fetchAgentLogsFailed: (state) => {
      state.agentLogs.loading = false;
    },

    // Plans
    fetchPlansRequest: (state) => {
      state.plans.loading = true;
    },
    fetchPlansSuccess: (state, action: PayloadAction<IGetCustomerPlansResponse["data"]>) => {
      state.plans.data = action.payload;
      state.plans.loading = false;
    },
    fetchPlansFailed: (state) => {
      state.plans.loading = false;
    },

    // Compliance Notes
    fetchComplianceNotesRequest: (state) => {
      state.complianceNotes.loading = true;
    },
    fetchComplianceNotesSuccess: (
      state,
      action: PayloadAction<IGetCustomerComplianceNotesResponse["data"]>
    ) => {
      state.complianceNotes.data = action.payload;
      state.complianceNotes.loading = false;
    },
    fetchComplianceNotesFailed: (state) => {
      state.complianceNotes.loading = false;
    },

    // Notes
    fetchNotesRequest: (state) => {
      state.notes.loading = true;
    },
    fetchNotesSuccess: (
      state,
      action: PayloadAction<{
        notes: IAccountNote[];
        count: number;
        countActive: number;
      }>
    ) => {
      state.notes.data.notes = action.payload.notes;
      state.notes.data.count = action.payload.count;
      state.notes.data.countActive = action.payload.countActive;
      state.notes.loading = false;
    },
    fetchNotesFailed: (state) => {
      state.notes.loading = false;
    },

    // Suspicious Notes
    fetchSuspiciousNotesRequest: (state) => {
      state.suspiciousNotes.loading = true;
    },
    fetchSuspiciousNotesSuccess: (
      state,
      action: PayloadAction<{
        notes: IAccountNote[];
        count: number;
        countActive: number;
      }>
    ) => {
      state.suspiciousNotes.data.notes = action.payload.notes;
      state.suspiciousNotes.data.count = action.payload.count;
      state.suspiciousNotes.data.countActive = action.payload.countActive;
      state.suspiciousNotes.loading = false;
    },
    fetchSuspiciousNotesFailed: (state) => {
      state.suspiciousNotes.loading = false;
    },

    // Management Data
    fetchManagementDataRequest: (state) => {
      state.managementData.loading = true;
    },
    fetchManagementDataSuccess: (
      state,
      action: PayloadAction<IGetAccountManagementDataResponse["data"]>
    ) => {
      state.managementData.data = action.payload;
      state.managementData.loading = false;
    },
    fetchManagementDataFailed: (state) => {
      state.managementData.loading = false;
    },

    // Payment Cards
    fetchPaymentCardsRequest: (state) => {
      state.payment_cards.loading = true;
    },
    fetchPaymentCardsSuccess: (
      state,
      action: PayloadAction<IGetCustomerPaymentCardsResponse["data"]>
    ) => {
      state.payment_cards.data.payment_cards.cards = action.payload.paymentCards.rows;
      state.payment_cards.data.payment_cards.count = action.payload.paymentCards.count;
      state.payment_cards.loading = false;
    },
    fetchPaymentCardsFailed: (state) => {
      state.payment_cards.loading = false;
    },

    // Main Data
    fetchMainDataRequest: (state) => {
      state.mainData.loading = true;
    },
    fetchMainDataSuccess: (state, action: PayloadAction<Partial<IGetCustomerResponse["data"]>>) => {
      state.mainData.data.banned_account = action.payload.banned_account || null;
      state.mainData.data.funded_trader = action.payload.funded_trader || null;
      state.mainData.loading = false;
    },
    fetchMainDataFailed: (state) => {
      state.mainData.loading = false;
    },

    // Shared IPs
    fetchSharedIpsRequest: (state) => {
      state.ipsData.sharedIps.loading = true;
    },
    fetchSharedIpsSuccess: (
      state,
      action: PayloadAction<TGetSharedIpsResponse["data"]["sharedIps"]>
    ) => {
      state.ipsData.sharedIps.data.rows = action.payload.rows;
      state.ipsData.sharedIps.data.count = action.payload.count;
      state.ipsData.sharedIps.loading = false;
    },
    fetchSharedIpsFailed: (state) => {
      state.ipsData.sharedIps.loading = false;
    },

    // Shared Reg IPs
    fetchSharedRegIpsRequest: (state) => {
      state.ipsData.sharedRegIp.loading = true;
    },
    fetchSharedRegIpsSuccess: (
      state,
      action: PayloadAction<IGetSharedRegIpsResponse["data"]["sharedRegIp"]>
    ) => {
      state.ipsData.sharedRegIp.data.rows = action.payload.rows;
      state.ipsData.sharedRegIp.data.count = action.payload.count;
      state.ipsData.sharedRegIp.loading = false;
    },
    fetchSharedRegIpsFailed: (state) => {
      state.ipsData.sharedRegIp.loading = false;
    },

    // Similar Funded Accounts
    fetchSimilarFundedAccountsRequest: (state) => {
      state.similarTradedAccounts.loading = true;
    },
    fetchSimilarFundedAccountsSuccess: (
      state,
      action: PayloadAction<IFindSimilarFundedAccountsResponse["data"]>
    ) => {
      state.similarTradedAccounts.data = action.payload;
      state.similarTradedAccounts.loading = false;
    },
    fetchSimilarFundedAccountsFailed: (state) => {
      state.similarTradedAccounts.loading = false;
    },
  },
});

export const customerActions = index.actions;
export default index.reducer;
